/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {addAdminNotification} from '@ion/applications/src/pages/admin/notifications/state';
import {adminNotificationsAddIsEditingSelector} from '@ion/applications/src/pages/admin/notifications/state-selectors';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  EventClassification,
  EventClassificationEntriesMap,
  EventCodeValueType,
  getEventCodesByClassification,
  getEventName
} from '@ion/core/src/services/EventCodes';
import {
  GenericObject,
  HVSRecord
} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {ReportQueryParams} from '@ion/core/src/types/services';
import {
  endOfDay,
  startOfDay
} from '@ion/core/src/utils/DateUtil';
import {isDeepEqual} from '@ion/core/src/utils/Equality';
import {toStringMapper} from '@ion/core/src/utils/StringUtil';
import {useFilterState} from '@ion/dls/src/filters/useFilterState';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {ALL_FEATURES_DEF, NONE_FEATURES_DEF} from '@ion/dls/src/maps/constants';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {TrackingFilters, TrackingMode} from '@ion/dls/src/types/types';
import {dateTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
import {verifyItems} from '@ion/features/src/asset-history/AssetHistoryGridUtils';
import {getAssetHistorySetting, savePreferences} from '@ion/ion-suite/src/hooks/useAssetHistorySettings';
import useAssetSnapToRoad from '@ion/ion-suite/src/hooks/useAssetSnapToRoad';
import {usePreferenceRefresh} from '@ion/ion-suite/src/hooks/usePreferenceRefresh';
import {
  formatter,
  groupAssetsRoutesHistoryByAsset
} from '@ion/ion-suite/src/routes/tracking2/helpers';
import Features, {TRIP, TRIP_LIST} from '@ion/ion-suite/src/security/Features';
import {getFilterSelections} from '@/components/filter/helpers';
import {
  getHarshEvents,
  getTotalAssetAndEvents,
  LoadingState,
  LoadingStateLabel
} from '@/routes/reports/reportsUtils';
import useAHRSettings from '@/routes/reports/useAHRSettings';
import useDeepLinkValidation from '@/routes/reports/useDeepLinkValidation';
import {TOP_MARGIN, useGridHeight} from '@/routes/tracking/hooks/sidesheet/useGridHeight';
import useBoundaryFilter from '@/routes/tracking/hooks/tracking/useBoundaryFilter';
import {mapCrashToHVSEvent} from '@/store/actions/helpers';
import {
  setHoveredHistoryEvent,
  setIsMobileMapView,
  setTimelineDateRange,
  toggleDistanceTravelledCard,
  toggleDrivingTimeCard,
  toggleErrorDialogue as _toggleErrorDialogue,
  toggleHarshEventsCard,
  toggleIdlingTimeCard,
  toggleShowEventsBetweenTrips,
  toggleShowSummaryCard,
  toggleShowSummaryCards,
  toggleShowTripVisible,
  toggleTimeSlider as _toggleTimeSlider,
  toggleTotalAssetsCard
} from '@/store/actions/reports';
import {updateAssetTrackingHistory} from '@/store/actions/tracking';
import {getReportParams} from '@/store/helpers';
import {ReportPreferences} from '@/store/reducers/reports';
import {
  globalGroupIdSelector,
  globalGroupStateSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {userSelector} from '@/store/selectors/login-selectors';
import {
  dateValueSelector,
  gridFilterModelSelector,
  isMobileMapViewSelector,
  isReportsInitializedSelector,
  reportsHistoryEndDateSelector,
  reportsHistoryStartDateSelector,
  reportsTimelineSelector,
  selectedDVRAssetsSelector
} from '@/store/selectors/reports-selectors';
import {
  historyActiveSelector,
  routeHistorySelector,
  savedGeofenceSelector,
  trackingFiltersSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {Settings} from '@/types/reports';
import {getHistoryReportRevision, subscribeHistoryReportRevision} from '../store';
import useAssetHistoryStyle from './useAssetHistoryStyle';

export interface AssetHistoryProps {
  gridRef: React.MutableRefObject<GenericObject>;
  isFMTracking?: boolean;
};

interface Props {
  current: number;
  limit?: number;
};

/*
 * Central provider hook for Asset History feature.
 *
 * This hook acts as a single orchestration layer for the entire Asset History module,
 * combining:
 *
 * - Redux state (filters, timeline, history, user, global settings)
 * - Local UI state (selection, hover, dialogs, loading, layout, filters)
 * - Derived computations (filtered history, event filters, card data, query params)
 * - Side effects (syncing filters, timeline interactions, loading lifecycle, UI resets)
 * - Action handlers (map/grid interactions, toggles, error handling, vision/video actions)
 *
 * It also splits concerns into grouped memoized outputs:
 *
 * - coreStateProps: base domain state + setters
 * - dataProps: computed/derived data for visualization
 * - uiStateProps: UI flags controlling rendering behavior
 * - handlerProps: event handlers and UI actions
 * - visionProps: ION Vision / video-related integration
 * - configProps: static/config-driven values and utilities
 *
 * The goal is to provide a single cohesive interface for the Asset History screen,
 * reducing prop drilling and keeping UI components declarative while all business logic
 * stays centralized and memoized for performance.
 */
const useAssetHistoryProvider = () => {
  const dispatch                 = useDispatch();
  const {isPhone}                = useMedia();
  const filters                  = useSelector(trackingFiltersSelector);
  const startDate                = useSelector(reportsHistoryStartDateSelector) || startOfDay();
  const endDate                  = useSelector(reportsHistoryEndDateSelector) || endOfDay();
  const isInitialized            = useSelector(isReportsInitializedSelector);
  const timeline                 = useSelector(reportsTimelineSelector);
  const routeHistory             = useSelector(routeHistorySelector);
  const user                     = useSelector(userSelector);
  const globalGroupState         = useSelector(globalGroupStateSelector);
  const savedGeofence            = useSelector(savedGeofenceSelector);
  const showAddNotification      = useSelector(adminNotificationsAddIsEditingSelector);
  const globalGroupId            = useSelector(globalGroupIdSelector);
  const dateValue                = useSelector(dateValueSelector);
  const isMobileMapView          = useSelector(isMobileMapViewSelector);
  const dvrSupportedAssets       = useSelector(selectedDVRAssetsSelector);
  const filterModel              = useSelector(gridFilterModelSelector);
  const isAssetSnapToRoadEnabled = useAssetSnapToRoad();
  const isHistoryActive          = useSelector(historyActiveSelector);
  const trackingMode             = useSelector(trackingModeSelector);
  const historyReportRevision    = useSyncExternalStore(
    subscribeHistoryReportRevision,
    getHistoryReportRevision,
    getHistoryReportRevision
  );
  // in the current theme, a History report the Time Mode control is running
  // is re-queried only once the control lets the change through - a filter
  // change it refuses over the limit leaves the previous results in place ...
  const isTimeModeReport = isCurrentTheme() && isHistoryActive && trackingMode === TrackingMode.History;
  const isPreferenceRefresh      = usePreferenceRefresh();
  // seeded synchronously from the persisted preference (rather than derived
  // from the AG-Grid filter model, which only reflects a change after an
  // async, debounced round trip through Redux) so this value - and anything
  // persisting from it - is reliable from the very first render ...
  const [showStatusEvents, setShowStatusEvents] = useState<boolean>(() => (
    getAssetHistorySetting()?.showStatusEvents !== false
  ));
  const {
    showEventsBetweenTrips,
    showTripVisible,
    showSummaryCards,
    showDistanceTravelled,
    showIdlingTime,
    showDrivingTime,
    showHarshEvents,
    showTotalAssets
  } = useAHRSettings();

  const {boundary}  = useBoundaryFilter({});
  const {useStyles} = useAssetHistoryStyle();

  const [history, setHistory]                           = useState<HVSRecord[] | undefined>();
  const [filterState, setFilterState]                   = useFilterState() as [TrackingFilters, Function];
  const initialHeight                                   = useGridHeight();
  const [isVisionRequest, setIsVisionRequest]           = useState(false);
  const [showErrorDialog, setShowErrorDialog]           = useState(false);
  const [isHistoryInitialized, setIsHistoryInitialized] = useState(false);
  const [timeSliderDisabled, setTimeSliderDisabled]     = useState(false);
  const [
    isShowNotificationSetupSnackbar,
    setIsShowNotificationSetupSnackbar
  ] = useState(false);
  const [showSettingsChooser, setShowSettingsChooser]   = useState(false);
  const [selectedTrip, setSelectedTrip]                 = useState(null);
  const [currentRequest, setCurrentRequest]             = useState({});
  const [eventData, setEventData]                       = useState(null);
  const [iOnVisionURL, setIONVisionURL]                 = useState(null);
  const [selectedMapMarkerData, setSelectedMapMarker]   = useState(null);
  const [hoveredEvent, setHoveredEvent]                 = useState<HVSRecord>(null);
  const [isHistoryDrawn, setIsHistoryDrawn]             = useState<boolean>(false);
  const [queryParams, setQueryParams]                   = useState<ReportQueryParams>({});
  const [state, setState]                               = useState<LoadingState>(LoadingState.Retrieving);
  const [filteredMapHistory, setFilteredMapHistory]     = useState<HVSRecord[] | undefined>([]);
  const [filteredGridHistory, setFilteredGridHistory]   = useState<HVSRecord[] | undefined>(null);
  const [showExitSelection, setShowExitSelection]       = useState<boolean>(false);
  const [totalDistance, setTotalDistance]               = useState(0);
  const [totalDriveTime, setTotalDriveTime]             = useState(0);
  const [totalIdleTime, setTotalIdleTime]               = useState(0);
  const [forceToRefresh, setForceToRefresh]             = useState(false);
  const [gridHeight, setGridHeight]                     = useState(initialHeight - TOP_MARGIN);
  const hasIOnVision                                    = useMemo(() => Features.hasIOnVision(), [Features]);
  const hasCrashboxx                                    = useMemo(() => Features.hasCrashboxx(), [Features]);
  const gridHeightRef                                   = useRef(gridHeight);
  const showEventsBetweenTripsRef                       = useRef(showEventsBetweenTrips);
  const showTripVisibleRef                              = useRef(showTripVisible);
  const preventTimeSliderCloseRef                       = useRef(false);
  const timeSliderRef                                   = useRef(timeline);
  const [assetsRoutesHistory, setAssetsRoutesHistory]   = useState({});

  const hasTripFeature          = Features.hasFeature(TRIP);
  const hasTripListFeature      = Features.hasFeature(TRIP_LIST);
  const isBoundaryFilterEnabled = !!boundary?.length;
  const empty                   = translate('reports:assetHistory.empty');
  const {preValidator}          = useDeepLinkValidation();
  const systemUnits             = useSystemOfUnits();

  const setTableDateString = useCallback((start: any, end: any) => {
    return `${dateTimeFormatter(start || startOfDay())} - ${dateTimeFormatter(end || endOfDay())}`;
  }, [dateTimeFormatter, startOfDay, endOfDay]);

  const [tableDates, setTableDates] = useState<string>(setTableDateString(startDate, endDate));
  const iOnPeriodicEvent = useMemo(() => getEventName({from: EventCodeValueType.eventType, val: 'PRIOD'}), []);

  const isTripServicesEnabled = useMemo(() => (
    hasTripFeature && hasTripListFeature
  ), [hasTripFeature, hasTripListFeature]);

  /*
   * Keeps a reference to the latest timeline instance.
   * Ensures the ref is always in sync with the current timeline object
   * for use in callbacks without causing re-renders.
   */
  useEffect(() => {
    timeSliderRef.current = timeline;
  }, [timeline]);

  /*
   * Updates table date display whenever filter state changes.
   * Extracts start and end dates from the filter state and formats them
   * into a human-readable string for the table UI.
   */
  useEffect(() => {
    if (filterState) {
      const {startDate, endDate} = filterState?.date || {};
      setTableDates(setTableDateString(startDate, endDate));
    }
  }, [dateValue, filterState, user]);

  /*
   * Builds and updates report query parameters whenever filter state changes.
   * If filters are not empty, it generates report parameters including boundary
   * information and updates the query state used for data fetching.
   */
  useEffect(() => {
    if (!isEmpty(filterState)) {
      const params = getReportParams({
        filters  : filterState,
        boundary : filterState?.boundary
      });
      setQueryParams(params);
    }
  }, [filterState]);

  /*
   * Syncs filter changes with local state and resets dependent UI state.
   * If filters meaningfully change, updates filter state, closes TimeSlider,
   * clears hovered event, and disables the TimeSlider to force recalculation.
   */
  const syncFilterState = () => {
    const currentSelected = getFilterSelections(filterState);
    const updatedSelected = getFilterSelections(filters);

    if (!isDeepEqual(currentSelected, updatedSelected)) {
      setFilterState(filters);
      timeline?.isTimeSliderOn && toggleTimeSlider();
      setHoveredEvent(null);
      setTimeSliderDisabled(true);
    }
  };

  useEffect(() => {
    if (!isTimeModeReport) {
      syncFilterState();
    }
  }, [filters]);

  /*
   * Syncs the filters a Time Mode report runs under once the control lets
   * it run - on mount as much as on each accepted change ...
   */
  useEffect(() => {
    if (isTimeModeReport) {
      syncFilterState();
    }
  }, [historyReportRevision]);

  /*
   * Synchronizes hovered event state with TimeSlider and selection UI.
   * If the TimeSlider is active and not blocked by a prevent flag, it is toggled off.
   * Also updates whether the exit-selection UI should be shown based on hovered event existence.
   * Resets the prevent flag after handling.
   */
  useEffect(() => {
    if (timeline.isTimeSliderOn && !preventTimeSliderCloseRef.current) {
      toggleTimeSlider();
    }
    setShowExitSelection(!isNil(hoveredEvent));
    preventTimeSliderCloseRef.current = false;
  }, [hoveredEvent]);

  /*
   * Processes and filters history data based on UI toggles and loading state.
   * Recomputes grid visibility when trip/event filters change or a refresh is forced.
   * Updates filtered history, timeline disabled state, and loading state accordingly.
   */
  useEffect(() => {
    const hasShowEventsChanged       = showEventsBetweenTrips !== showEventsBetweenTripsRef.current;
    const hasShowTripVisibleChanged  = showTripVisible !== showTripVisibleRef.current;
    const hasShowTripOrEventsChanged = hasShowEventsChanged || hasShowTripVisibleChanged;
    const shouldProcess              =
      state === LoadingState.Retrieved ||
      hasShowTripOrEventsChanged ||
      forceToRefresh;
  
    setForceToRefresh(false);
    
    if (shouldProcess) {
      if (!history) {
        setTimeSliderDisabled(true);
        setFilteredGridHistory([]);
        setState(() => LoadingState.Processed);
        setToDoneOn(true);
        return;
      }
  
      const filteredHistory = history.filter(({gridVisibility, tripGroup}) => {
        if (gridVisibility) {
          if (!showEventsBetweenTrips && showTripVisible) {
            return !!tripGroup;
          }
          return true;
        }
        return false;
      });
  
      const isDisabled = filteredHistory.length === 0;
  
      setTimeSliderDisabled(isDisabled);
      setFilteredGridHistory(filteredHistory);
      setState(() => LoadingState.Processed);
      setToDoneOn(true);
  
      showEventsBetweenTripsRef.current = showEventsBetweenTrips;
      showTripVisibleRef.current        = showTripVisible;
    }
  }, [history, timeline, showEventsBetweenTrips, showTripVisible, forceToRefresh]);

  /*
   * Initializes history state on component mount.
   * Ensures the "isHistoryInitialized" flag is set to true
   * only once when the component is first rendered.
   */
  useEffect(() => {
    !isHistoryInitialized && setIsHistoryInitialized(true);
  }, []);

  /*
   * Persists the Asset History settings-panel toggles to the user's
   * preferences whenever one changes, so they're retained across sessions.
   */
  useEffect(() => {
    const current: Partial<ReportPreferences> = {
      showSummaryCards,
      showTripVisible,
      showEventsBetweenTrips,
      showTotalAssets,
      showDistanceTravelled,
      showDrivingTime,
      showIdlingTime,
      showHarshEvents,
      isAssetSnapToRoadEnabled,
      showStatusEvents
    };
    savePreferences(current);
    return () => {
      savePreferences.flush();
    };
  }, [
    showSummaryCards,
    showTripVisible,
    showEventsBetweenTrips,
    showTotalAssets,
    showDistanceTravelled,
    showDrivingTime,
    showIdlingTime,
    showHarshEvents,
    isAssetSnapToRoadEnabled,
    showStatusEvents
  ]);

  /*
   * Called by HistoryHeader after it applies a Status Event toggle to the
   * grid - updates the reliable, locally-owned showStatusEvents value (seeded
   * from the persisted preference above) so the save effect above and the
   * value passed back down to HistoryHeader both reflect it immediately,
   * rather than waiting on the grid filter's own round trip through Redux.
   */
  const onStatusEventToggled = useCallback((nextShowStatusEvents: boolean) => {
    setShowStatusEvents(nextShowStatusEvents);
  }, []);

  /*
   * Handles grid resize completion with debounce to avoid excessive updates.
   * Calculates the new grid height, persists it in user preferences,
   * and updates both state and ref for consistent UI behavior.
   */
  const onResizeStop = useCallback(debounce(height => {
    const calculated = height - TOP_MARGIN;
    setGridHeight(calculated);
    UserPreferences.set(Preference.trackingBottomGridHeight, calculated, true);
    gridHeightRef.current = calculated;
  }, 300), []);

  /*
   * Stores assets and routes history grouped by assetId.
   * Uses the first asset in the history array to derive the key,
   * and merges the new entry into the existing state safely.
   */
  const onSetAssetsRoutesHistory = useCallback((
    assetsHistory: HVSRecord[],
    routesHistory: GenericObject
  ) => {
    if (assetsHistory && routesHistory && assetsHistory.length > 0) {
      if (isCurrentTheme()) {
        const entries = groupAssetsRoutesHistoryByAsset(assetsHistory, routesHistory);
        setAssetsRoutesHistory(prevState => ({
          ...prevState,
          ...entries
        }));
      } else {
        const {assetId} = assetsHistory[0];

        setAssetsRoutesHistory(prevState => ({
          ...prevState,
          [`id-${assetId}`] : {assetsHistory, routesHistory}
        }));
      }
    }
  }, []);

  /*
   * Resets the assets routes history state to an empty object.
   * Clears all previously stored route history data for assets.
   */
  const resetAssetsRoutesHistory = useCallback(() => {
    setAssetsRoutesHistory({});
  }, []);

  /*
   * Toggles the TimeSlider visibility in the global store.
   * Dispatches the action responsible for opening/closing the TimeSlider UI.
   */
  const toggleTimeSlider = useCallback(() => {
    dispatch(_toggleTimeSlider());
  }, []);

  /*
   * Toggles the TimeSlider UI while ensuring safe state transitions.
   * Prevents immediate closure triggers, exits any active selection,
   * and then toggles the TimeSlider visibility.
   */
  const onToggleTimeSlider = useCallback(() => {
    preventTimeSliderCloseRef.current = true;
    onExitSelection();
    toggleTimeSlider();
  }, []);

  /*
   * Transitions the loading state to "Done" after a short delay.
   * On mobile devices (or when forced), waits 1.2s before updating state
   * to allow animations or UI transitions to complete smoothly.
   */
  const setToDoneOn = (force = false) => {
    if (isPhone || force) {
      const timeout = setTimeout(() => {
        setState(() => LoadingState.Done);
        clearTimeout(timeout);
      }, 1200);
    }
  };

  /*
   * Sets ION Vision data for the selected asset.
   * Extracts the video URL from the asset and updates both
   * the vision URL state and the associated event data.
   */
  const setIONVision = useCallback(asset => {
    const {VideoURL} = asset;
    setIONVisionURL(VideoURL);
    setEventData(asset);
  }, []);

  /*
   * Handles click on the "find icon" in the map UI.
   * Sets the selected trip based on the clicked row from the grid or list.
   */
  const handleMapFindIconClick = useCallback(selectedRow => {
    setSelectedTrip(selectedRow);
  }, []);

  /*
   * Handles exit from the current selection mode.
   * Clears the hovered event state and disables the exit selection UI.
   */
  const onExitSelection = useCallback(() => {
    setHoveredEvent(null as any);
    setShowExitSelection(false);
  }, []);

  /*
   * Toggles the mobile map view mode on or off.
   * Dispatches the updated inverse state to the store.
   */
  const getAlignedRoutes = useCallback(assets => {
    dispatch(updateAssetTrackingHistory(assets));
  }, []);

  const toggleMobileMapView = useCallback(() => {
    dispatch(setIsMobileMapView(!isMobileMapView));
  }, [isMobileMapView]);

  /*
   * Builds the list of severe event codes based on the translated label
   * for the "Severe" event classification.
   * This is used to map UI classification labels to backend event codes.
   */
  const severeEventsList = getEventCodesByClassification(
    translate(EventClassificationEntriesMap[EventClassification.Severe])
  );

  /*
   * Updates the timeline date range in the store.
   * Dispatches the selected start and end dates to update the global timeline state.
   */
  const setTimeline = useCallback((start, end) => {
    dispatch(setTimelineDateRange(start, end));
  }, [setTimelineDateRange]);

  /*
   * Toggles the error dialog visibility and stores the associated request context.
   * Updates local state to control error UI display and track the current failing request.
   */
  const onToggleErrorDialog = useCallback((isError, currentRequest = {}) => {
    // in the current theme a request refused over the limit is answered by
    // the Time Mode control's own "History wasn't updated" dialog, and the
    // report keeps showing its previous results - so no error is raised here ...
    if (!(isError && isCurrentTheme())) {
      setShowErrorDialog(isError);
      setCurrentRequest(currentRequest);
    }
  }, [setShowErrorDialog, setCurrentRequest]);

  /*
   * Marks the history layer as fully drawn after a debounce delay.
   * Prevents rapid state updates by waiting for rendering to settle
   * before setting the "isHistoryDrawn" flag.
   */
  const onLayerDrawn = useCallback(debounce(() => {
    setIsHistoryDrawn(true);
  }, 1000), []);

  /*
   * Handles selection of a map marker.
   * Stores the selected marker item in state for highlighting
   * or further interaction within the map view.
   */
  const handleSelectedMarker = useCallback((item, graphic, coordinates) => {
    setSelectedMapMarker(item);
  }, []);

  /*
   * Toggles the global error dialogue visibility state.
   * Dispatches an action with the current request context
   * to properly track and display error-related information.
   */
  const toggleErrorDialogue = useCallback(show => {
    dispatch(_toggleErrorDialogue(show, currentRequest));
  }, [currentRequest]);

  /*
   * Closes all active snackbar and error UI states.
   * Resets both local and global error dialog visibility flags,
   * and hides the notification setup snackbar.
   */
  const onCloseSnackBar = useCallback(() => {
    setShowErrorDialog(false);
    toggleErrorDialogue(false);
    setIsShowNotificationSetupSnackbar(false);
  }, []);

  /*
   * Handles notification setup confirmation flow.
   * Hides the notification setup snackbar and triggers
   * the admin notification creation action.
   */
  const onSetupNotification = useCallback(() => {
    setIsShowNotificationSetupSnackbar(false);
    dispatch(addAdminNotification());
  }, []);

  /*
   * Toggles the visibility of the settings chooser panel.
   * Flips the current open/closed state of the settings chooser UI.
   */
  const toggleSettingsChooser = useCallback(() => {
    setShowSettingsChooser(!showSettingsChooser);
  }, [showSettingsChooser]);

  /*
   * Handles hover interactions coming from the history grid.
   * When a row is hovered, ensures the time slider is turned off (if active)
   * and updates the currently hovered event state.
   */
  const onHoveredEventFromGrid = useCallback(data => {
    timeSliderRef.current?.isTimeSliderOn && toggleTimeSlider();
    setHoveredEvent(data);
  }, [timeline]);

  /*
   * Clears the currently hovered history event when the mouse leaves the grid.
   * Resets hover state to ensure no event remains highlighted.
   */
  const handleGridMouseOutEvent = useCallback(() => {
    setHoveredHistoryEvent(null);
  }, []);

  /*
   * Handles a request to load or display video/vision data from an external event.
   * Marks the vision request as active and stores the incoming event payload.
   */
  const handleRequestVideo = useCallback(eventData => {
    const {data} = eventData || {};
    setIsVisionRequest(true);
    setEventData(data);
  }, []);

  /*
   * Toggles the visibility of all summary cards in the dashboard.
   * Dispatches a Redux action to switch the summary cards display state.
   */
  const onToggleShowSummaryCards = useCallback(() => {
    dispatch(toggleShowSummaryCards());
  }, []);

  /*
   * Toggles the visibility of trip-related data in the store.
   * Dispatches the updated visibility state to Redux.
   */
  const onToggleShowTripVisible = useCallback(state => {
    dispatch(toggleShowTripVisible(state));
  }, []);

  /*
   * Toggles the "show events between trips" setting in the store.
   * Dispatches a Redux action to switch the current visibility state.
   */
  const onToggleShowEventsBetweenTrips = useCallback(() => {
    dispatch(toggleShowEventsBetweenTrips());
  }, []);

  /*
   * Retrieves the total number of harsh events from the filtered grid history.
   * Applies validation on items before processing and returns the count of matches.
   */
  const retrieveHarshEvents = useCallback(() => {
    const harshMatches = getHarshEvents(verifyItems(filteredGridHistory || []));
    return harshMatches.length;
  }, [filteredGridHistory, getHarshEvents, verifyItems]);

  /*
   * Safely resolves a data point from history using the provided resolver function.
   * If history is empty or undefined, returns a default empty value.
   * Prevents unnecessary computations when no historical data is available.
   */
  const getDataPoint = useCallback((resolver: Function) => {
    return history?.length ? resolver() : empty;
  }, [history]);
  
  /*
   * Aggregates summary card data used in the dashboard view.
   * Computes total assets and event counts from history and maps
   * calculated values into their respective settings keys for rendering.
   */
  const cardData = useMemo(() => {
    const [totalAssets] = getTotalAssetAndEvents(history, showEventsBetweenTrips);
    return ({
      [Settings.TotalDistanceCard]   : totalDistance,
      [Settings.TotalIdleCard]       : totalIdleTime,
      [Settings.TotalDrivenTimeCard] : totalDriveTime,
      [Settings.TotalHarshCard]      : getDataPoint(retrieveHarshEvents),
      [Settings.TotalAssetsCard]     : totalAssets || empty
    });
  }, [history, totalDistance, showEventsBetweenTrips, filteredGridHistory]);

  /*
   * Builds the feature layer filter definition based on selected event filters.
   * Applies an SQL WHERE clause when event values are selected, otherwise
   * falls back to showing all features or no features depending on the filter state.
   */
  const featureFilter = useMemo(() => {
    const events = filterModel?.event?.values as string[] | undefined;
    if (events?.length) {
      return {
        where : `LOWER(iOnEventName) IN (${toStringMapper(events, formatter, true)})`
      };
    }
    return {where: isNil(events) ? ALL_FEATURES_DEF : NONE_FEATURES_DEF};
  }, [filterModel, formatter]);

  /*
   * Determines whether the event filter is actively filtering events.
   * Returns true when the current filter differs from the default
   * "all features" definition and does not include periodic events.
   */
  const isEventFilterActive = useMemo(() => {
    const query  = featureFilter?.where || '';
    return query !== ALL_FEATURES_DEF && !query?.includes(iOnPeriodicEvent?.toLowerCase());
  }, [featureFilter, iOnPeriodicEvent]);

  /*
   * Maps summary setting keys to their corresponding toggle actions.
   * Used to dynamically dispatch the correct Redux action
   * when a summary card setting is enabled or disabled.
   */
  const settingToggleActions = useMemo(() => ({
    [Settings.DistanceTravelled] : toggleDistanceTravelledCard,
    [Settings.IdlingTime]        : toggleIdlingTimeCard,
    [Settings.DrivingTime]       : toggleDrivingTimeCard,
    [Settings.HarshEvents]       : toggleHarshEventsCard,
    [Settings.TotalAssetsCard]   : toggleTotalAssetsCard
  }), []);

  /*
   * Handles summary setting changes by batching related Redux updates.
   * Updates the summary card visibility based on the parent setting value
   * and triggers the corresponding toggle action for the selected setting.
   */
  const onSummarySettingChanged = useCallback((name, value, parentValue) => {
    batch(() => {
      dispatch(toggleShowSummaryCard(parentValue));
      dispatch(settingToggleActions[name]?.());
    });
  }, [settingToggleActions]);

  /*
   * Displays the Asset History message content, including title, description,
   * sub-content, and the current vs. maximum allowed asset history limit.
   */
  const MessageContent = memo(({current, limit = 40}: Props) => {
    const classes = useStyles();
    return (
      <div>
        <b><span className={classes.title}>{translate('reports:assetHistory.title')}</span></b>
        <span className={classes.content}>{translate('reports:assetHistory.content')}</span>
        <p>
          <span className={classes.content}>{translate('reports:assetHistory.subContent')}</span>
          <span className={classes.content}>{translate('reports:assetHistory.limitContent', {limit, current})}</span>
        </p>
      </div>
    );
  });

  const permissionProps = useMemo(() => ({
    isTripServicesEnabled
  }), [isTripServicesEnabled]);

  const toggleProps = useMemo(() => ({
    onToggleShowSummaryCards,
    onToggleShowTripVisible,
    onToggleShowEventsBetweenTrips,
    showSummaryCards,
    showTripVisible,
    showEventsBetweenTrips,
    onSummarySettingChanged,
    summaryChildrenChecked : {
      showDistanceTravelled,
      showIdlingTime,
      showDrivingTime,
      showHarshEvents,
      showTotalAssets
    }
  }), [
    showTripVisible,
    showSummaryCards,
    showEventsBetweenTrips,
    showDistanceTravelled,
    showIdlingTime,
    showDrivingTime,
    showHarshEvents,
    showTotalAssets
  ]);

  const coreStateProps = useMemo(() => ({
    state,
    filters,
    history,
    timeline,
    boundary,
    setState,
    setHistory,
    setTimeline,
    queryParams,
    systemUnits,
    isInitialized,
    globalGroupId,
    globalGroupState
  }), [
    state,
    filters,
    history,
    timeline,
    boundary,
    setState,
    setHistory,
    setTimeline,
    queryParams,
    systemUnits,
    isInitialized,
    globalGroupId,
    globalGroupState
  ]);

  const dataProps = useMemo(() => ({
    cardData,
    eventData,
    routeHistory,
    hoveredEvent,
    selectedTrip,
    totalIdleTime,
    totalDistance,
    totalDriveTime,
    severeEventsList,
    filteredMapHistory,
    filteredGridHistory,
    assetsRoutesHistory,
    selectedMapMarkerData,
    dvrSupportedAssets
  }), [
    cardData,
    eventData,
    routeHistory,
    hoveredEvent,
    selectedTrip,
    totalIdleTime,
    totalDistance,
    totalDriveTime,
    severeEventsList,
    filteredMapHistory,
    filteredGridHistory,
    assetsRoutesHistory,
    selectedMapMarkerData,
    dvrSupportedAssets
  ]);

  const uiStateProps = useMemo(() => ({
    showIdlingTime,
    showTripVisible,
    showHarshEvents,
    showTotalAssets,
    showDrivingTime,
    showErrorDialog,
    showSummaryCards,
    showExitSelection,
    showStatusEvents,
    showSettingsChooser,
    showAddNotification,
    showDistanceTravelled,
    showEventsBetweenTrips,
    isMobileMapView,
    isHistoryDrawn,
    isVisionRequest,
    isEventFilterActive,
    isPreferenceRefresh,
    isHistoryInitialized,
    isBoundaryFilterEnabled,
    isAssetSnapToRoadEnabled,
    isShowNotificationSetupSnackbar
  }), [
    showIdlingTime,
    showTripVisible,
    showHarshEvents,
    showTotalAssets,
    showDrivingTime,
    showErrorDialog,
    showSummaryCards,
    showExitSelection,
    showStatusEvents,
    showSettingsChooser,
    showAddNotification,
    showDistanceTravelled,
    showEventsBetweenTrips,
    isMobileMapView,
    isHistoryDrawn,
    isVisionRequest,
    isEventFilterActive,
    isPreferenceRefresh,
    isHistoryInitialized,
    isBoundaryFilterEnabled,
    isAssetSnapToRoadEnabled,
    isShowNotificationSetupSnackbar
  ]);

  const handlerProps = useMemo(() => ({
    onResizeStop,
    onLayerDrawn,
    onExitSelection,
    setHoveredEvent,
    onCloseSnackBar,
    toggleTimeSlider,
    setTotalDistance,
    setTotalIdleTime,
    getAlignedRoutes,
    setTotalDriveTime,
    setIsHistoryDrawn,
    setTableDateString,
    handleRequestVideo,
    onToggleTimeSlider,
    toggleMobileMapView,
    onToggleErrorDialog,
    onSetupNotification,
    handleSelectedMarker,
    toggleSettingsChooser,
    setFilteredMapHistory,
    handleMapFindIconClick,
    onHoveredEventFromGrid,
    onStatusEventToggled,
    handleGridMouseOutEvent,
    resetAssetsRoutesHistory,
    onSetAssetsRoutesHistory
  }), [
    onResizeStop,
    onLayerDrawn,
    onExitSelection,
    setHoveredEvent,
    onCloseSnackBar,
    toggleTimeSlider,
    setTotalDistance,
    setTotalIdleTime,
    getAlignedRoutes,
    setTotalDriveTime,
    setIsHistoryDrawn,
    setTableDateString,
    handleRequestVideo,
    onToggleTimeSlider,
    toggleMobileMapView,
    onToggleErrorDialog,
    onSetupNotification,
    handleSelectedMarker,
    onStatusEventToggled,
    toggleSettingsChooser,
    setFilteredMapHistory,
    handleMapFindIconClick,
    onHoveredEventFromGrid,
    handleGridMouseOutEvent,
    resetAssetsRoutesHistory,
    onSetAssetsRoutesHistory
  ]);

  const visionProps = useMemo(() => ({
    setIONVision,
    hasIOnVision,
    iOnVisionURL,
    setIONVisionURL,
    setIsVisionRequest,
    mapCrashToHVSEvent
  }), [
    setIONVision,
    hasIOnVision,
    iOnVisionURL,
    setIONVisionURL,
    setIsVisionRequest,
    mapCrashToHVSEvent
  ]);

  const configProps = useMemo(() => ({
    Settings,
    useStyles,
    gridHeight,
    tableDates,
    toggleProps,
    hasCrashboxx,
    preValidator,
    LoadingState,
    initialHeight,
    featureFilter,
    savedGeofence,
    MessageContent,
    currentRequest,
    iOnPeriodicEvent,
    LoadingStateLabel,
    setForceToRefresh,
    timeSliderDisabled
  }), [
    Settings,
    useStyles,
    gridHeight,
    tableDates,
    toggleProps,
    hasCrashboxx,
    preValidator,
    LoadingState,
    initialHeight,
    featureFilter,
    savedGeofence,
    MessageContent,
    currentRequest,
    iOnPeriodicEvent,
    LoadingStateLabel,
    setForceToRefresh,
    timeSliderDisabled
  ]);

  return {
    dataProps,
    configProps,
    visionProps,
    uiStateProps,
    handlerProps,
    coreStateProps,
    permissionProps
  };
};

export default useAssetHistoryProvider;
